import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ManualIssueListItem } from '@accestra/types';
import Badge, { type BadgeVariant } from '../ui/Badge';
import Button from '../ui/Button';
import { formatDateTime } from '../../i18n/formatters';
import { downloadManualIssuesCsv } from '../../lib/manual-issue-csv';

const TABLE = {
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    textAlign: 'left' as const,
    padding: '14px 18px',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    color: 'var(--acc-color-text-soft)',
    borderBottom: '1px solid var(--acc-color-border)',
    background: 'var(--surface-secondary)',
  },
  td: {
    padding: '16px 18px',
    borderBottom: '1px solid var(--acc-color-border)',
    verticalAlign: 'top' as const,
  },
};

type ManualIssueSortKey =
  | 'title_asc'
  | 'title_desc'
  | 'severity_desc'
  | 'severity_asc'
  | 'status_asc'
  | 'status_desc'
  | 'created_desc'
  | 'created_asc'
  | 'updated_desc'
  | 'updated_asc';

function severityVariant(severity: ManualIssueListItem['severity']): BadgeVariant {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'serious':
      return 'warning';
    case 'moderate':
      return 'info';
    case 'minor':
    default:
      return 'neutral';
  }
}

function issueTypeVariant(issueType: ManualIssueListItem['issueType']): BadgeVariant {
  switch (issueType) {
    case 'usability_issue':
      return 'info';
    case 'wcag_issue':
    default:
      return 'neutral';
  }
}

function statusVariant(status: ManualIssueListItem['status']): BadgeVariant {
  switch (status) {
    case 'resolved':
      return 'success';
    case 'in_progress':
      return 'warning';
    case 'open':
    default:
      return 'neutral';
  }
}

function severityRank(severity: ManualIssueListItem['severity']): number {
  switch (severity) {
    case 'critical':
      return 4;
    case 'serious':
      return 3;
    case 'moderate':
      return 2;
    case 'minor':
    default:
      return 1;
  }
}

function statusRank(status: ManualIssueListItem['status']): number {
  switch (status) {
    case 'open':
      return 3;
    case 'in_progress':
      return 2;
    case 'resolved':
    default:
      return 1;
  }
}

interface ManualIssuesTableProps {
  issues: ManualIssueListItem[];
  showSiteColumn?: boolean;
  caption: string;
  exportScopeName?: string;
  onEdit?(issue: ManualIssueListItem): void;
}

function resolveIssueType(issueType: ManualIssueListItem['issueType'] | undefined): ManualIssueListItem['issueType'] {
  return issueType === 'usability_issue' ? 'usability_issue' : 'wcag_issue';
}

export default function ManualIssuesTable({
  issues,
  showSiteColumn = false,
  caption,
  exportScopeName = 'workspace',
  onEdit,
}: ManualIssuesTableProps) {
  const { t, i18n } = useTranslation(['common']);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<'all' | ManualIssueListItem['severity']>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | ManualIssueListItem['status']>('all');
  const [sortKey, setSortKey] = useState<ManualIssueSortKey>('updated_desc');
  const [exportMessage, setExportMessage] = useState<string | null>(null);

  const filteredIssues = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase(i18n.language);
    const matchesSearch = (issue: ManualIssueListItem) => {
      if (!normalizedSearch) {
        return true;
      }

      const title = issue.title.toLocaleLowerCase(i18n.language);
      const description = issue.description.toLocaleLowerCase(i18n.language);
      const structuredFields = [
        issue.affectedPageUrl,
        issue.wcagVersion,
        issue.wcagCriterion,
        issue.recommendedFix,
        issue.reviewBatch,
        issue.siteName,
      ]
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
        .join(' ')
        .toLocaleLowerCase(i18n.language);

      return title.includes(normalizedSearch) ||
        description.includes(normalizedSearch) ||
        structuredFields.includes(normalizedSearch);
    };

    const next = issues.filter((issue) => {
      if (severityFilter !== 'all' && issue.severity !== severityFilter) {
        return false;
      }

      if (statusFilter !== 'all' && issue.status !== statusFilter) {
        return false;
      }

      return matchesSearch(issue);
    });

    next.sort((left, right) => {
      switch (sortKey) {
        case 'title_asc':
          return left.title.localeCompare(right.title, i18n.language);
        case 'title_desc':
          return right.title.localeCompare(left.title, i18n.language);
        case 'severity_desc':
          return severityRank(right.severity) - severityRank(left.severity);
        case 'severity_asc':
          return severityRank(left.severity) - severityRank(right.severity);
        case 'status_asc':
          return statusRank(right.status) - statusRank(left.status);
        case 'status_desc':
          return statusRank(left.status) - statusRank(right.status);
        case 'created_asc':
          return new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
        case 'created_desc':
          return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
        case 'updated_asc':
          return new Date(left.updatedAt).getTime() - new Date(right.updatedAt).getTime();
        case 'updated_desc':
        default:
          return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
      }
    });

    return next;
  }, [i18n.language, issues, searchTerm, severityFilter, sortKey, statusFilter]);

  const handleExport = () => {
    if (filteredIssues.length === 0) {
      setExportMessage(t('manualIssues.exportCsvEmpty'));
      return;
    }

    downloadManualIssuesCsv(filteredIssues, exportScopeName);
    setExportMessage(t('manualIssues.exportCsvSucceeded'));
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
          padding: '0 18px',
        }}
      >
        <label className="acc-form-modal__field">
          <span className="acc-form-modal__label">{t('manualIssues.searchLabel')}</span>
          <input
            type="search"
            className="acc-input"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t('manualIssues.searchPlaceholder')}
          />
        </label>

        <label className="acc-form-modal__field">
          <span className="acc-form-modal__label">{t('manualIssues.severityFilterLabel')}</span>
          <select
            className="acc-select"
            value={severityFilter}
            onChange={(event) => setSeverityFilter(event.target.value as 'all' | ManualIssueListItem['severity'])}
          >
            <option value="all">{t('manualIssues.filterAllSeverities')}</option>
            <option value="critical">{t('manualIssues.severity.critical')}</option>
            <option value="serious">{t('manualIssues.severity.serious')}</option>
            <option value="moderate">{t('manualIssues.severity.moderate')}</option>
            <option value="minor">{t('manualIssues.severity.minor')}</option>
          </select>
        </label>

        <label className="acc-form-modal__field">
          <span className="acc-form-modal__label">{t('manualIssues.statusFilterLabel')}</span>
          <select
            className="acc-select"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as 'all' | ManualIssueListItem['status'])}
          >
            <option value="all">{t('manualIssues.filterAllStatuses')}</option>
            <option value="open">{t('manualIssues.status.open')}</option>
            <option value="in_progress">{t('manualIssues.status.in_progress')}</option>
            <option value="resolved">{t('manualIssues.status.resolved')}</option>
          </select>
        </label>

        <label className="acc-form-modal__field">
          <span className="acc-form-modal__label">{t('manualIssues.sortLabel')}</span>
          <select
            className="acc-select"
            value={sortKey}
            onChange={(event) => setSortKey(event.target.value as ManualIssueSortKey)}
          >
            <option value="updated_desc">{t('manualIssues.sort.updated_desc')}</option>
            <option value="updated_asc">{t('manualIssues.sort.updated_asc')}</option>
            <option value="created_desc">{t('manualIssues.sort.created_desc')}</option>
            <option value="created_asc">{t('manualIssues.sort.created_asc')}</option>
            <option value="title_asc">{t('manualIssues.sort.title_asc')}</option>
            <option value="title_desc">{t('manualIssues.sort.title_desc')}</option>
            <option value="severity_desc">{t('manualIssues.sort.severity_desc')}</option>
            <option value="severity_asc">{t('manualIssues.sort.severity_asc')}</option>
            <option value="status_asc">{t('manualIssues.sort.status_asc')}</option>
            <option value="status_desc">{t('manualIssues.sort.status_desc')}</option>
          </select>
        </label>

        <div className="acc-form-modal__field" style={{ justifyContent: 'flex-end' }}>
          <span className="acc-form-modal__label">{t('manualIssues.exportLabel')}</span>
          <Button
            type="button"
            variant="secondary"
            onClick={handleExport}
            disabled={filteredIssues.length === 0}
            aria-label={t('manualIssues.exportCsvAriaLabel')}
          >
            {t('manualIssues.exportCsvAction')}
          </Button>
          <span style={{ fontSize: 12, color: 'var(--acc-color-text-muted)', lineHeight: 1.5 }}>
            {filteredIssues.length === 0
              ? t('manualIssues.exportCsvEmpty')
              : t('manualIssues.exportCsvHint')}
          </span>
        </div>
      </div>

      {exportMessage && (
        <div
          role="status"
          aria-live="polite"
          style={{
            padding: '0 18px',
            fontSize: 13,
            color: 'var(--acc-color-text-muted)',
          }}
        >
          {exportMessage}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={TABLE.table} className="acc-data-table">
          <caption className="acc-sr-only">{caption}</caption>
          <thead>
            <tr>
              <th scope="col" style={TABLE.th}>{t('manualIssues.columns.title')}</th>
              <th scope="col" style={TABLE.th}>{t('manualIssues.columns.page')}</th>
              <th scope="col" style={TABLE.th}>{t('manualIssues.columns.wcag')}</th>
              <th scope="col" style={TABLE.th}>{t('manualIssues.columns.description')}</th>
              {showSiteColumn && <th scope="col" style={TABLE.th}>{t('manualIssues.columns.site')}</th>}
              <th scope="col" style={TABLE.th}>{t('manualIssues.columns.type')}</th>
              <th scope="col" style={TABLE.th}>{t('manualIssues.columns.severity')}</th>
              <th scope="col" style={TABLE.th}>{t('manualIssues.columns.status')}</th>
              <th scope="col" style={TABLE.th}>{t('manualIssues.columns.created')}</th>
              <th scope="col" style={TABLE.th}>{t('manualIssues.columns.updated')}</th>
              {onEdit && <th scope="col" style={TABLE.th}>{t('manualIssues.columns.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {filteredIssues.length === 0 ? (
              <tr>
                <td
                  colSpan={9 + (showSiteColumn ? 1 : 0) + (onEdit ? 1 : 0)}
                  style={{ ...TABLE.td, color: 'var(--acc-color-text-muted)' }}
                >
                  {t('manualIssues.noResults')}
                </td>
              </tr>
            ) : filteredIssues.map((issue) => (
              <tr key={issue.id}>
                <th scope="row" style={{ ...TABLE.td, minWidth: 240, color: 'var(--acc-color-text)' }}>
                  <span style={{ display: 'grid', gap: 6 }}>
                    <span style={{ fontWeight: 700 }}>{issue.title}</span>
                    {issue.reviewBatch && (
                      <span style={{ fontSize: 12, color: 'var(--acc-color-text-muted)', lineHeight: 1.4 }}>
                        {t('manualIssues.reviewBatchInlineLabel')}: {issue.reviewBatch}
                      </span>
                    )}
                  </span>
                </th>
                <td style={{ ...TABLE.td, minWidth: 160, color: 'var(--acc-color-text-muted)', wordBreak: 'break-word' as const }}>
                  {issue.affectedPageUrl ?? '—'}
                </td>
                <td style={{ ...TABLE.td, minWidth: 210 }}>
                  {issue.wcagVersion || issue.wcagCriterion ? (
                    <span style={{ display: 'grid', gap: 4 }}>
                      {issue.wcagVersion && (
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--acc-color-text)' }}>
                          {issue.wcagVersion}
                        </span>
                      )}
                      {issue.wcagCriterion && (
                        <span style={{ fontSize: 12, color: 'var(--acc-color-text-muted)', lineHeight: 1.4 }}>
                          {issue.wcagCriterion}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--acc-color-text-muted)' }}>—</span>
                  )}
                </td>
                <td style={{ ...TABLE.td, minWidth: 320 }}>
                  <span style={{ display: 'grid', gap: 8, fontSize: 13, color: 'var(--acc-color-text-muted)', lineHeight: 1.55 }}>
                    {issue.description}
                    {issue.recommendedFix && (
                      <span>
                        <strong style={{ color: 'var(--acc-color-text)' }}>
                          {t('manualIssues.recommendedFixInlineLabel')}:
                        </strong>{' '}
                        {issue.recommendedFix}
                      </span>
                    )}
                  </span>
                </td>
                {showSiteColumn && (
                  <td style={TABLE.td}>
                    <div
                      aria-label={`${issue.siteName} – ${issue.siteBaseUrl}`}
                      style={{ display: 'grid', gap: 4 }}
                    >
                      <span aria-hidden="true" style={{ fontWeight: 600 }}>{issue.siteName}</span>
                      <span aria-hidden="true" className="acc-sr-only"> – </span>
                      <span
                        aria-hidden="true"
                        style={{ fontSize: 12, color: 'var(--acc-color-text-muted)', wordBreak: 'break-all' as const }}
                      >
                        {issue.siteBaseUrl}
                      </span>
                    </div>
                  </td>
                )}
                <td style={TABLE.td}>
                  <Badge variant={issueTypeVariant(resolveIssueType(issue.issueType))}>
                    {t(`manualIssues.issueType.${resolveIssueType(issue.issueType)}`)}
                  </Badge>
                </td>
                <td style={TABLE.td}>
                  <Badge variant={severityVariant(issue.severity)}>
                    {t(`manualIssues.severity.${issue.severity}`)}
                  </Badge>
                </td>
                <td style={TABLE.td}>
                  <Badge variant={statusVariant(issue.status)}>
                    {t(`manualIssues.status.${issue.status}`)}
                  </Badge>
                </td>
                <td style={{ ...TABLE.td, color: 'var(--acc-color-text-muted)' }}>
                  {formatDateTime(issue.createdAt) ?? '—'}
                </td>
                <td style={{ ...TABLE.td, color: 'var(--acc-color-text-muted)' }}>
                  {formatDateTime(issue.updatedAt) ?? '—'}
                </td>
                {onEdit && (
                  <td style={{ ...TABLE.td, width: 120 }}>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => onEdit(issue)}
                      aria-label={t('manualIssues.editActionAriaLabel', { title: issue.title })}
                    >
                      {t('manualIssues.editAction')}
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
